import { useEffect, useState } from "react";
import { Instagram, Copy, Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const usePostImage = (guideId, postId, imageId) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (!postId) return undefined;
    let obj;
    api.get(`/guides/${guideId}/social/${postId}/image`, { responseType: "blob" })
      .then((r) => { obj = URL.createObjectURL(r.data); setUrl(obj); }).catch(() => setUrl(null));
    return () => obj && URL.revokeObjectURL(obj);
  }, [guideId, postId, imageId]);
  return url;
};

const useSlides = (guideId, post) => {
  const [urls, setUrls] = useState([]);
  useEffect(() => {
    const n = post?.slides?.length || 0;
    if (!n) { setUrls([]); return undefined; }
    let objs = [];
    Promise.all(Array.from({ length: n }, (_, i) => api.get(`/guides/${guideId}/social/${post.id}/slides/${i + 1}`, { responseType: "blob" })))
      .then((rs) => { objs = rs.map((r) => URL.createObjectURL(r.data)); setUrls(objs); })
      .catch(() => setUrls([]));
    return () => objs.forEach((u) => URL.revokeObjectURL(u));
  }, [guideId, post?.id, post?.slides?.join()]); // eslint-disable-line react-hooks/exhaustive-deps
  return urls;
};

const Carousel = ({ guide, post, urls }) => {
  const [busy, setBusy] = useState(false);
  const zip = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/guides/${guide.id}/social/${post.id}/carousel.zip`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `${guide.town.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-instagram-carousel-ed${post.edition}.zip`;
      a.click();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  if (!urls.length) return null;
  return (
    <div className="mt-6 pt-6 border-t border-[#E2D9CC]" data-testid="social-carousel">
      <div className="flex items-center mb-3">
        <p className="eyebrow">Carousel · {urls.length} slides</p>
        <Button data-testid="social-carousel-download-button" variant="outline" onClick={zip} disabled={busy} className="ml-auto rounded-none h-9"><Download className="w-4 h-4 mr-2" />{busy ? "Preparing…" : "Download carousel (ZIP)"}</Button>
      </div>
      <div className="grid grid-cols-6 gap-2">
        {urls.map((u, i) => <img key={u} data-testid={`social-slide-${i + 1}`} src={u} alt={`Slide ${i + 1}`} className="w-full aspect-[4/5] object-cover border border-[#E2D9CC]" />)}
      </div>
      <p className="text-xs text-[#8C827A] mt-2">Post the slides in order: cover, three chapters, "Inside the guide", then "Read Chapter I free". Prices are in the caption only. The ZIP includes caption.txt.</p>
    </div>
  );
};

export const SocialPostCard = ({ guide, onCreated }) => {
  const post = (guide.social_posts || []).slice(-1)[0];
  const img = usePostImage(guide.id, post?.id, post?.image_id);
  const slides = useSlides(guide.id, post);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (post) setText(`${post.caption}\n.\n.\n${post.hashtags.join(" ")}`); }, [post?.id, post?.caption]); // eslint-disable-line react-hooks/exhaustive-deps

  const create = async () => {
    setBusy(true);
    try {
      await api.post(`/guides/${guide.id}/social`);
      toast.success("New Instagram post drafted");
      onCreated();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const download = () => {
    const a = document.createElement("a");
    a.href = img;
    a.download = `${guide.town.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-instagram-ed${post.edition}.jpg`;
    a.click();
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-px bg-[#E2D9CC] border border-[#E2D9CC] mb-10" data-testid="social-post-card">
      <div className="bg-[var(--g-dark,#3B1B48)] grid place-items-center p-4 min-h-[200px]">
        {img ? <img data-testid="social-post-image" src={img} alt={post.alt_text} className="w-full aspect-[4/5] object-cover" />
          : <Instagram className="w-8 h-8 text-[var(--g-accent,#C59B27)] opacity-60" strokeWidth={1.2} />}
      </div>
      <div className="bg-[#FAF7F2] p-7">
        <div className="flex items-center gap-3 mb-3">
          <Instagram className="w-5 h-5 text-[var(--g-accent,#C59B27)]" strokeWidth={1.4} />
          <p className="eyebrow">Instagram post</p>
          {post && <span data-testid="social-post-kind" className="ml-auto text-[11px] font-mono-accent uppercase tracking-wider text-[#8C827A]">{post.kind === "launch" ? "New guide" : `Edition ${post.edition} update`} · {post.hashtags.length} hashtags</span>}
        </div>
        {post ? (
          <>
            <Textarea data-testid="social-post-caption" rows={9} value={text} onChange={(e) => setText(e.target.value)} className="bg-white rounded-none text-sm mb-4" />
            <div className="flex flex-wrap gap-3">
              <Button data-testid="social-copy-button" onClick={() => navigator.clipboard.writeText(text).then(() => toast.success("Caption and hashtags copied"))} className="rounded-none"><Copy className="w-4 h-4 mr-2" />Copy caption + hashtags</Button>
              <Button data-testid="social-download-button" variant="outline" onClick={download} disabled={!img} className="rounded-none"><Download className="w-4 h-4 mr-2" />Download image (1080×1350)</Button>
              <Button data-testid="social-regenerate-button" variant="ghost" onClick={create} disabled={busy} className="rounded-none"><RefreshCw className="w-4 h-4 mr-2" />{busy ? "Writing…" : "New version"}</Button>
            </div>
            <Carousel guide={guide} post={post} urls={slides} />
          </>
        ) : (
          <>
            <p className="text-sm text-[#1C181D] mb-4">A post is drafted automatically whenever a guide is built or updated. Create one now for this edition.</p>
            <Button data-testid="social-create-button" onClick={create} disabled={busy} className="rounded-none"><Instagram className="w-4 h-4 mr-2" />{busy ? "Writing…" : "Create Instagram post"}</Button>
          </>
        )}
      </div>
    </div>
  );
};
